import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URLS } from '../../config/api';

const CompanyList = () => {
    const navigate = useNavigate();
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    
    // Add filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedLocation, setSelectedLocation] = useState('');
    const [selectedIndustry, setSelectedIndustry] = useState('');
    const [selectedSize, setSelectedSize] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    
    // State for unique values
    const [locations, setLocations] = useState([]);
    const [industries, setIndustries] = useState([]);
    const [companySizes, setCompanySizes] = useState([]);

    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                const response = await fetch(API_URLS.companies);
                if (!response.ok) {
                    throw new Error('Failed to fetch companies');
                }
                const data = await response.json();
                setCompanies(data);
                
                // Extract unique values
                setLocations([...new Set(data.map(company => company.location))].filter(Boolean));
                setIndustries([...new Set(data.map(company => company.industry))].filter(Boolean));
                setCompanySizes([...new Set(data.map(company => company.size))].filter(Boolean));
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchCompanies();
    }, []);

    const filteredCompanies = companies.filter(company => {
        const searchFields = [
            company.name,
            company.industry,
            company.location,
            company.size,
            company.website,
            company.description,
            company.contact_person,
            company.contact_email,
            company.contact_phone,
            company.status
        ].map(field => (field || '').toLowerCase());

        const search = searchTerm.toLowerCase();
        const matchesSearch = searchTerm === '' || searchFields.some(field => field.includes(search));
            
        const matchesLocation = selectedLocation === '' || 
            company.location === selectedLocation;
            
        const matchesIndustry = selectedIndustry === '' || 
            company.industry === selectedIndustry;
            
        const matchesSize = selectedSize === '' || 
            company.size === selectedSize;
            
        const matchesStatus = selectedStatus === '' || 
            company.status === selectedStatus;
            
        return matchesSearch && matchesLocation && matchesIndustry && 
               matchesSize && matchesStatus;
    });

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this company?')) {
            return;
        }

        setIsDeleting(true);
        try {
            const response = await fetch(API_URLS.deleteCompany(id), {
                method: 'DELETE'
            });

            if (!response.ok) {
                throw new Error('Failed to delete company');
            }

            // Refresh companies list
            const updatedCompanies = companies.filter(company => company.id !== id);
            setCompanies(updatedCompanies);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsDeleting(false);
        }
    };

    if (loading) return <div className="p-8">Loading...</div>;
    if (error) return <div className="p-8 text-red-600">Error: {error}</div>;

    return (
        <div className="p-8 bg-gray-50">
			<div className="flex justify-between items-center mb-6">
			    <div>
					<h1 className="text-2xl font-bold text-gray-900">Companies & Recruiters</h1>
					<p className="mt-1 text-sm text-gray-500">
						Total Companies: {filteredCompanies.length}
                </p>
				</div>
                <button
                    onClick={() => navigate('/admin/companies/new')}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                    Add New Company
                </button>
            </div>

            {/* Enhanced Search and Filters */}
            <div className="bg-white p-4 rounded-lg shadow mb-6">
                <div className="grid grid-cols-1 gap-4 mb-4">
                    <input
                        type="text"
                        placeholder="Search in all fields (name, industry, contact, etc.)..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                    />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <select
                        value={selectedIndustry}
                        onChange={(e) => setSelectedIndustry(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                    >
                        <option value="">All Industries</option>
                        {industries.map(industry => (
                            <option key={industry} value={industry}>{industry}</option>
                        ))}
                    </select>

                    <select
                        value={selectedLocation}
                        onChange={(e) => setSelectedLocation(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                    >
                        <option value="">All Locations</option>
                        {locations.map(location => (
                            <option key={location} value={location}>{location}</option>
                        ))}
                    </select>

                    <select
                        value={selectedSize}
                        onChange={(e) => setSelectedSize(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                    >
                        <option value="">All Company Sizes</option>
                        {companySizes.map(size => (
                            <option key={size} value={size}>{size}</option>
                        ))}
                    </select>

                    <select
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                    >
                        <option value="">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </select>
                </div>
            </div>

            {/* Companies Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Company
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Industry
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Location
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Size
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Jobs Posted
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredCompanies.map((company) => (
                            <tr key={company.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="font-medium text-gray-900">{company.name}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                                    {company.industry}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                                    {company.location}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                                    {company.size}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                        company.status === 'active' 
                                            ? 'bg-green-100 text-green-800' 
                                            : 'bg-red-100 text-red-800'
                                    }`}>
                                        {company.status}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                                    {company.total_jobs}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <button 
                                        onClick={() => {
                                            console.log('Editing company:', company.id);
                                            navigate(`/admin/companies/${company.id}/edit`);
                                        }}
                                        className="text-purple-600 hover:text-purple-900 mr-4"
                                    >
                                        Edit
                                    </button>
                                    <button 
                                        onClick={() => handleDelete(company.id)}
                                        disabled={isDeleting}
                                        className="text-red-600 hover:text-red-900"
                                    >
                                        {isDeleting ? 'Deleting...' : 'Delete'}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CompanyList; 